import React from 'react';
import { useTranslation } from '@shared/core';
import { CircleColoredIcon } from '../../components/CircleColoredIcon';
import { FilterList } from '../../components/FilterBase';
import { PhotoCount, ThemeVariant } from './Template.types';
import { getTemplatePhotoCount } from './Template.util';

export const TEMPLATE_DEFAULT_VENDOR = 'Joy';
export const TEMPLATE_COLUMN_COUNT_MOBILE = 2;
export const TEMPLATE_COLUMN_COUNT_DESKTOP = 4;
export const TEMPLATE_GUTTER_DESKTOP = 40;
export const TEMPLATE_GUTTER_MOBILE = 24;
export const TEMPLATE_ASPECT_RATIO = 1.242;
export interface TemplateFilterGroups extends FilterList {
  getValuesFromTemplate: (template: ThemeVariant) => string[];
}

export const useInitialFilterItems = (): TemplateFilterGroups[] => {
  const { t } = useTranslation('websiteDesigner');
  const filterLabels = t('filterLabels');
  const filterItems = t('filterItems');

  return [
    {
      label: filterLabels.layout(),
      getValuesFromTemplate: template => [template.variationType],
      items: [
        { label: filterItems.layout.singlePage(), value: ['aloha'] },
        { label: filterItems.layout.multiPage(), value: ['brannan'] },
      ],
    },
    {
      label: filterLabels.categories(),
      getValuesFromTemplate: template => template.tags,
      items: [
        { label: filterItems.categories.bold(), value: ['Bold', 'Geometric', 'Bright', 'Typographic'] },
        { label: filterItems.categories.classic(), value: ['Formal', 'Elegant', 'Romantic', 'Classic', 'Art Deco'] },
        { label: filterItems.categories.cultural(), value: ['Cultural', 'Religious', 'Indian', 'Indonesian', 'Chinese', 'Japanese', 'Mexican'] },
        { label: filterItems.categories.destination(), value: ['Destination', 'Beach', 'Mountains', 'Desert', 'Tropical', 'Nautical', 'City'] },
        { label: filterItems.categories.floralBotanical(), value: ['Floral', 'Botanical'] },
        { label: filterItems.categories.metallic(), value: ['Metallic'] },
        { label: filterItems.categories.modern(), value: ['Modern', 'Geometric', 'Minimal', 'Black & White', 'Typographic'] },
        { label: filterItems.categories.rustic(), value: ['Rustic', 'Country', 'Mountains', 'Forest'] },
        { label: filterItems.categories.seasonal(), value: ['Seasonal', 'Summer', 'Winter', 'Fall', 'Spring'] },
        { label: filterItems.categories.textured(), value: ['Textured', 'Watercolor', 'Paper', 'Marble', 'Paint'] },
      ],
    },
    {
      label: filterLabels.colors(),
      getValuesFromTemplate: template => [...template.colors, template.variantColor].filter(Boolean) as string[],
      items: [
        { label: filterItems.colors.red(), value: ['Red'], icon: <CircleColoredIcon background="#8A1F1F" /> },
        { label: filterItems.colors.orange(), value: ['Orange'], icon: <CircleColoredIcon background="#F09E62" /> },
        { label: filterItems.colors.yellow(), value: ['Yellow'], icon: <CircleColoredIcon background="#FFE798" /> },
        { label: filterItems.colors.green(), value: ['Green'], icon: <CircleColoredIcon background="#627154" /> },
        { label: filterItems.colors.blue(), value: ['Blue'], icon: <CircleColoredIcon background="#416497" /> },
        { label: filterItems.colors.brown(), value: ['Brown'], icon: <CircleColoredIcon background="#796051" /> },
        { label: filterItems.colors.purple(), value: ['Purple'], icon: <CircleColoredIcon background="#866D8E" /> },
        { label: filterItems.colors.pink(), value: ['Pink'], icon: <CircleColoredIcon background="#F8DBDA" /> },
        { label: filterItems.colors.beige(), value: ['Beige'], icon: <CircleColoredIcon background="#F0E8E2" /> },
        { label: filterItems.colors.gray(), value: ['Gray'], icon: <CircleColoredIcon background="#E0E0E0" /> },
        { label: filterItems.colors.black(), value: ['Black'], icon: <CircleColoredIcon background="#333333" /> },
        {
          label: filterItems.colors.white(),
          value: ['White'],
          icon: (
            <CircleColoredIcon
              background="#FFFFFF"
              borderColor="mono5"
            />
          ),
        },
        {
          label: filterItems.colors.gold(),
          value: ['Gold'],
          icon: <CircleColoredIcon background="linear-gradient(135deg, #8F5303 0%, #E4C269 28.65%, #FFE68A 42.71%, #F0D16F 55.21%, #BD8A1C 75%, #C59320 84.37%, #AB7613 100%)" />,
        },
        {
          label: filterItems.colors.roseGold(),
          value: ['Rose Gold'],
          icon: (
            <CircleColoredIcon background="linear-gradient(135deg, #89392D 0%, #DF9C7A 28.65%, #FFCBAF 42.71%, #E5A280 55.21%, #A65C47 72.92%, #9B4F3E 84.36%, #87392F 100%)" />
          ),
        },
        {
          label: filterItems.colors.silver(),
          value: ['Silver'],
          icon: <CircleColoredIcon background="linear-gradient(135deg, #585550 0%, #908F8B 28.65%, #E5E5E5 42.71%, #BEBDBB 55.21%, #6C6965 75%, #6E6B66 84.37%, #585550 100%)" />,
        },
      ],
    },
    {
      label: filterLabels.photo(),
      getValuesFromTemplate: template => [getTemplatePhotoCount(template)],
      items: [
        { label: filterItems.photo.onePhoto(), value: [PhotoCount.One] },
        { label: filterItems.photo.moreThanTwoPhotos(), value: [PhotoCount.TwoOrMore] },
        { label: filterItems.photo.noPhoto(), value: [PhotoCount.Zero] },
      ],
    },
    {
      label: filterLabels.collection(),
      getValuesFromTemplate: template => [template.vendor],
      items: [
        { label: filterItems.collection.designedByJoy(), value: ['Joy'] },
        {
          label: filterItems.collection.rachelZoe(),
          value: ['Rachel Zoe'],
          badge: {
            text: t('badges').new(),
            color: 'white',
            backgroundColor: 'buttonProductiveHover',
          },
        },
        { label: filterItems.collection.paperCulture(), value: ['Paper Culture'] },
        { label: filterItems.collection.paperlust(), value: ['Paperlust'] },
      ],
    },
  ];
};

export const TEMPLATES_SEARCH_BY = ['variationType', 'vendor', 'tags', 'colors', 'font', 'variantColor', 'variantName', 'variantThemeId', 'theme.name', 'theme.themeId'];

export const UPLOADED_TEMPLATE_PREVIEW_IMAGE_KEY = 'templatePreviewImage';
